import React, { useState, useEffect, useRef } from 'react'
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ClockIcon,
  ChartBarIcon,
  UserIcon,
  ComputerDesktopIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { chatbotAPI } from '@/services/api'

interface ChatMessage {
  id: number
  message: string
  response: string
  type: 'financial_query' | 'transaction_analysis' | 'general' | 'advice'
  confidence: number
  created_at: string
  user_id: number
  session_id?: string
}

interface ChatSession {
  id: string
  title: string
  message_count: number
  last_message_at: string
  created_at: string
}

interface ChatbotStats {
  total_conversations: number
  total_messages: number
  average_response_time: number
  satisfaction_rate: number
  most_common_topics: Array<{
    topic: string
    count: number
  }>
  today_messages: number
}

interface ChatUIMessage {
  id: string
  content: string
  sender: 'user' | 'bot'
  timestamp: Date
  type?: string
  confidence?: number
  ai_provider?: string
}

const ChatbotPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatUIMessage[]>([])
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [stats, setStats] = useState<ChatbotStats | null>(null)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchChatbotData = async () => {
      try {
        setLoading(true)
        // Connect to REAL API endpoints - remove mock data usage
        const [sessionsData, statsData] = await Promise.all([
          chatbotAPI.getSessions(),
          chatbotAPI.getStats()
        ])
        setSessions(sessionsData)
        setStats(statsData)

        // Load messages from first session if exists
        if (sessionsData.length > 0) {
          const messagesData = await chatbotAPI.getMessages(sessionsData[0].id)
          const convertedMessages: ChatUIMessage[] = messagesData.map(msg => ([
            {
              id: `${msg.id}-user`,
              content: msg.message,
              sender: 'user' as const,
              timestamp: new Date(msg.created_at),
              type: msg.type
            },
            {
              id: `${msg.id}-bot`,
              content: msg.response,
              sender: 'bot' as const,
              timestamp: new Date(msg.created_at),
              type: msg.type,
              confidence: msg.confidence
            }
          ])).flat()
          setMessages(convertedMessages)
          setCurrentSessionId(sessionsData[0].id)
        }
        setError(null)
      } catch (err: any) {
        console.error('Chatbot API error:', err)
        setError(`Erro ao carregar dados do chatbot: ${err.message}`)
        // On error, set empty state instead of mock data
        setSessions([])
        setStats({
          total_conversations: 0,
          total_messages: 0,
          average_response_time: 0,
          satisfaction_rate: 0,
          most_common_topics: [],
          today_messages: 0
        })
        setMessages([])
        setCurrentSessionId(null)
      } finally {
        setLoading(false)
      }
    }

    fetchChatbotData()
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return

    const userMessage: ChatUIMessage = {
      id: `temp-${Date.now()}`,
      content: inputMessage,
      sender: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setSending(true)
    const messageToSend = inputMessage
    setInputMessage('')

    try {
      const response = await chatbotAPI.sendMessage({
        message: messageToSend,
        session_id: currentSessionId || undefined,
        context: {
          current_page: 'chatbot',
          user_data: {}
        }
      })

      const botMessage: ChatUIMessage = {
        id: `bot-${Date.now()}`,
        content: response.response,
        sender: 'bot',
        timestamp: new Date(),
        type: response.type,
        confidence: response.confidence,
        ai_provider: response.ai_provider
      }

      setMessages(prev => [...prev, botMessage])

      // Update session ID if new session was created
      if (!currentSessionId && response.session_id) {
        setCurrentSessionId(response.session_id)
      }
    } catch (err: any) {
      // Add error message as bot response for mock demo
      const errorMessage: ChatUIMessage = {
        id: `bot-error-${Date.now()}`,
        content: `Desculpe, houve um erro na conexão com o backend real: ${err.message}. Esta é uma demonstração usando dados mockados.`,
        sender: 'bot',
        timestamp: new Date(),
        type: 'error'
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getMessageTypeColor = (type?: string) => {
    switch (type) {
      case 'financial_query': return 'bg-blue-100 text-blue-800'
      case 'transaction_analysis': return 'bg-green-100 text-green-800'
      case 'advice': return 'bg-purple-100 text-purple-800'
      case 'error': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getProviderBadge = (provider?: string) => {
    if (!provider) return null

    const badges: Record<string, { color: string; label: string; icon: string }> = {
      'gemini': {
        color: 'bg-blue-100 text-blue-800 border border-blue-200',
        label: 'Gemini',
        icon: '🔵'
      },
      'claude': {
        color: 'bg-purple-100 text-purple-800 border border-purple-200',
        label: 'Claude',
        icon: '🟣'
      },
      'openai': {
        color: 'bg-green-100 text-green-800 border border-green-200',
        label: 'GPT',
        icon: '🟢'
      },
      'rules': {
        color: 'bg-gray-100 text-gray-700 border border-gray-200',
        label: 'Regras',
        icon: '⚙️'
      }
    }

    const badge = badges[provider.toLowerCase()] || badges['rules']

    return (
      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${badge.color} ml-2`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    )
  }

  const displayStats = stats || {
    total_conversations: 0,
    total_messages: 0,
    average_response_time: 0,
    satisfaction_rate: 0,
    most_common_topics: [],
    today_messages: 0
  }
  const displaySessions = sessions

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="h-96 bg-gray-100 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-7 h-7 text-indigo-600" />
            Assistente IA Financeiro
          </h1>
          <p className="text-gray-600">Chat inteligente para consultas e análises financeiras</p>
        </div>
        <button
          onClick={() => {/* TODO: Create new session */}}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-700 hover:to-purple-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Conversa
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-yellow-800 text-sm">{error}</p>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Area */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">FinBot IA</h3>
                  <p className="text-sm text-gray-500">Assistente Financeiro Inteligente</p>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {messages.length} mensagens
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.sender === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="text-sm">{message.content}</p>
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    <p className={`text-xs ${
                      message.sender === 'user' ? 'text-indigo-200' : 'text-gray-500'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                    <div className="flex items-center gap-2">
                      {message.type && message.sender === 'bot' && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getMessageTypeColor(message.type)}`}>
                          {message.confidence ? `${Math.round(message.confidence * 100)}%` : message.type}
                        </span>
                      )}
                      {message.sender === 'bot' && message.ai_provider && getProviderBadge(message.ai_provider)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="animate-pulse">Digitando...</div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua pergunta sobre finanças..."
                className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={1}
                disabled={sending}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || sending}
                className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Estatísticas</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Conversas:</span>
                <span className="text-sm font-medium">{displayStats.total_conversations}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Mensagens:</span>
                <span className="text-sm font-medium">{displayStats.total_messages}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Hoje:</span>
                <span className="text-sm font-medium">{displayStats.today_messages}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Satisfação:</span>
                <span className="text-sm font-medium">{displayStats.satisfaction_rate}/5</span>
              </div>
            </div>
          </div>

          {/* Sessions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Conversas Recentes</h3>
            <div className="space-y-2">
              {displaySessions.slice(0, 5).map((session) => (
                <div
                  key={session.id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    session.id === currentSessionId
                      ? 'bg-indigo-50 border border-indigo-200'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setCurrentSessionId(session.id)}
                >
                  <div className="flex justify-between items-start">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {session.title}
                    </h4>
                    <button className="text-gray-400 hover:text-red-500 ml-2">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {session.message_count} mensagens
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(session.last_message_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
            <div className="space-y-2">
              <button className="w-full text-left p-2 rounded-lg hover:bg-gray-50 text-sm">
                💰 Analisar gastos do mês
              </button>
              <button className="w-full text-left p-2 rounded-lg hover:bg-gray-50 text-sm">
                📊 Criar relatório financeiro
              </button>
              <button className="w-full text-left p-2 rounded-lg hover:bg-gray-50 text-sm">
                💡 Dicas de economia
              </button>
              <button className="w-full text-left p-2 rounded-lg hover:bg-gray-50 text-sm">
                🎯 Planejar metas financeiras
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatbotPage